import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import { RecalculateResponse } from './types';

type Metadata = RecalculateResponse['metadata'];

/** Human label for what the plan came from: a template override, the assigned pay policy, or nothing. */
export const planLabel = (metadata: Pick<Metadata, 'policy' | 'payPolicy'>): string => {
  if (metadata.policy) return `template: ${metadata.policy}`;
  if (metadata.payPolicy) return `pay policy: ${metadata.payPolicy}`;
  return 'no pay policy applied to this range';
};

const StageChips = ({ metadata }: { metadata: Metadata }) => (
  <Box>
    <Typography variant="subtitle2">
      Plan: <code>{planLabel(metadata)}</code>
    </Typography>
    <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5} mt={0.5}>
      {metadata.stages.map((stage, i) => (
        <Stack key={i} direction="row" alignItems="center" gap={0.5}>
          {i > 0 && <Typography color="text.secondary">→</Typography>}
          {stage.map((pluginId) => (
            <Chip key={pluginId} size="small" variant={metadata.autoLoaded.includes(pluginId) ? 'outlined' : 'filled'} label={`${pluginId}@${metadata.versions[pluginId] ?? '?'}`} title={metadata.autoLoaded.includes(pluginId) ? 'auto-loaded dependency' : 'root plugin'} />
          ))}
        </Stack>
      ))}
    </Stack>
  </Box>
);

const WrittenChips = ({ metadata }: { metadata: Metadata }) => (
  <Box>
    <Typography variant="subtitle2">Persisted by this run</Typography>
    <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5} data-testid="written-chip">
      {Object.entries(metadata.snapshotsWritten ?? {}).map(([kind, count]) => (
        <Chip key={kind} size="small" variant="outlined" color={count > 0 ? 'primary' : 'default'} label={`${kind}: ${count}`} title="source snapshot rows written" />
      ))}
      <Chip size="small" variant="outlined" color={metadata.summariesWritten?.shifts ? 'primary' : 'default'} label={`shift summaries: ${metadata.summariesWritten?.shifts ?? 0}`} />
      <Chip size="small" variant="outlined" color={metadata.summariesWritten?.days ? 'primary' : 'default'} label={`day summaries: ${metadata.summariesWritten?.days ?? 0}`} />
      {(metadata.payPolicySnapshots ?? []).map((snap) => (
        <Chip key={snap.snapshotUuid} size="small" variant="outlined" color={snap.written ? 'primary' : 'default'} label={`snapshot ${snap.snapshotUuid} · ${snap.written ? 'written' : 'reused'}`} title={`pay policy ${snap.payPolicyUuid}`} />
      ))}
    </Stack>
  </Box>
);

export const PlanProvenanceView = ({ metadata }: { metadata: Metadata }) => (
  <Stack spacing={1.5}>
    <Box>
      <Typography variant="subtitle2">Sources fetched</Typography>
      <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5}>
        {Object.entries(metadata.sources).map(([kind, count]) => (
          <Chip key={kind} size="small" color={count > 0 ? 'primary' : 'default'} label={`${kind}: ${count}`} />
        ))}
      </Stack>
    </Box>
    <Divider />
    <StageChips metadata={metadata} />
    <Divider />
    <WrittenChips metadata={metadata} />
  </Stack>
);
